/**
 * V7-B (14.06.2026) — Experten-Konsens: viele YouTube-Stimmen → EINE einfache Zeile.
 *
 * Ziel (User, wörtlich sinngemäß): den Marktkonsens zusammenfassen, dem
 * Nebenberufler Arbeit abnehmen, in EINFACHER Sprache — und NICHT 20 Listen
 * unter den Chart. Pro Wert genau eine verdichtete Aussage + ein Ton.
 *
 * LEITPLANKE (User): Hinweis-Schicht, bewegt NIE den Score. Immer mit
 * Kanal-Name (Attribution gegen Bias — der Hobbytrader wägt selbst).
 * Reine Funktionen, voll testbar. Lädt selbst nichts.
 */

export type ExpertStance = 'bullish' | 'neutral' | 'bearish';

/** Ton für die eine verdichtete Zeile (mappt auf die StatusDot-Sprache). */
export type ConsensusTone = 'positiv' | 'negativ' | 'uneinig' | 'neutral' | 'keine';

export interface ExpertOpinionInput {
  channelName: string;
  /** fundamental_macro | elliott | metals_macro_elliott | news_momentum */
  angle?: string | null;
  stance: ExpertStance;
  /** HKCM-Stil: „Top bei Region X" — prüfbarer Wegpunkt, kein Geschwätz. */
  levelNote?: string | null;
  summary?: string | null;
  /** ISO-Datum des Videos. */
  videoPublishedAt: string;
  videoUrl?: string | null;
}

export interface ExpertConsensus {
  tone: ConsensusTone;
  /** Genau EIN Satz, einfache Sprache (das, was unter dem Wert steht). */
  satz: string;
  /** Optionaler prüfbarer Level-Hinweis (eigene kleine Zeile, sonst null). */
  levelHinweis: string | null;
  bullish: number;
  bearish: number;
  neutral: number;
  /** Anzahl Experten, die sich geäußert haben (genau 1 Stimme je Kanal). */
  experten: number;
  /** Details nur zum Aufklappen — NICHT Teil der verdichteten Zeile. */
  stimmen: ExpertOpinionInput[];
}

/** Fachbegriff → Hobbytrader-Wort (einfache Sprache). */
const ANGLE_LABEL: Record<string, string> = {
  fundamental_macro: 'Fundamental',
  elliott: 'Charttechnik',
  metals_macro_elliott: 'Edelmetall/Makro',
  news_momentum: 'News',
};

function angleLabel(a?: string | null): string | null {
  if (!a) return null;
  return ANGLE_LABEL[a] ?? null;
}

function sideLabel(o: ExpertOpinionInput): string {
  const a = angleLabel(o.angle);
  return a ? `${a} (${o.channelName})` : o.channelName;
}

function shorten(text: string, max = 90): string {
  const t = text.trim();
  return t.length <= max ? t : `${t.slice(0, max - 1).trimEnd()}…`;
}

function uneinigSatz(stimmen: ExpertOpinionInput[]): string {
  const bear = stimmen.find((s) => s.stance === 'bearish');
  const bull = stimmen.find((s) => s.stance === 'bullish');
  const bearSide = bear ? sideLabel(bear) : 'die einen';
  const bullSide = bull ? sideLabel(bull) : 'die anderen';
  return `Experten uneinig — ${bearSide}: Vorsicht, ${bullSide}: positiv`;
}

/**
 * Verdichtet die Aussagen zu EINEM Wert auf eine einfache Zeile + Ton.
 * - je Kanal zählt nur die jüngste Stimme (ein Kanal = eine Stimme)
 * - nur frische Aussagen (Standard: 30 Tage)
 * - Uneinigkeit wird ehrlich gezeigt, aber knapp
 */
export function summarizeExpertOpinions(
  opinions: ExpertOpinionInput[],
  opts: { maxAgeDays?: number; now?: Date } = {}
): ExpertConsensus {
  const maxAgeDays = opts.maxAgeDays ?? 30;
  const now = opts.now ?? new Date();
  const cutoff = now.getTime() - maxAgeDays * 86_400_000;

  // 1) nur frische Aussagen
  const fresh = opinions.filter((o) => {
    const t = Date.parse(o.videoPublishedAt);
    return Number.isFinite(t) && t >= cutoff;
  });

  // 2) je Kanal nur die jüngste Stimme
  const latestByChannel = new Map<string, ExpertOpinionInput>();
  for (const o of fresh) {
    const prev = latestByChannel.get(o.channelName);
    if (!prev || Date.parse(o.videoPublishedAt) > Date.parse(prev.videoPublishedAt)) {
      latestByChannel.set(o.channelName, o);
    }
  }
  const stimmen = [...latestByChannel.values()].sort(
    (a, b) => Date.parse(b.videoPublishedAt) - Date.parse(a.videoPublishedAt)
  );

  const bullish = stimmen.filter((s) => s.stance === 'bullish').length;
  const bearish = stimmen.filter((s) => s.stance === 'bearish').length;
  const neutral = stimmen.filter((s) => s.stance === 'neutral').length;
  const total = stimmen.length;

  // 3) prüfbarer Level-Hinweis (jüngste Stimme mit konkretem Level)
  const withLevel = stimmen.find((s) => !!(s.levelNote && s.levelNote.trim()));
  const levelHinweis =
    withLevel && withLevel.levelNote ? `${withLevel.channelName}: ${withLevel.levelNote.trim()}` : null;

  if (total === 0) {
    return { tone: 'keine', satz: '', levelHinweis: null, bullish: 0, bearish: 0, neutral: 0, experten: 0, stimmen: [] };
  }

  let tone: ConsensusTone;
  let satz: string;

  if (total === 1) {
    const s = stimmen[0]!;
    tone = s.stance === 'bullish' ? 'positiv' : s.stance === 'bearish' ? 'negativ' : 'neutral';
    const wort = s.stance === 'bullish' ? 'sieht positiv' : s.stance === 'bearish' ? 'ist vorsichtig' : 'ist neutral';
    satz = `${s.channelName} ${wort}${s.summary ? `: ${shorten(s.summary)}` : ''}`;
  } else if (bullish > 0 && bearish > 0) {
    if (bullish === bearish) {
      tone = 'uneinig';
      satz = uneinigSatz(stimmen);
    } else if (bullish > bearish) {
      tone = 'positiv';
      satz = `${bullish} von ${total} Experten positiv${bearish ? `, ${bearish} vorsichtig` : ''}`;
    } else {
      tone = 'negativ';
      satz = `${bearish} von ${total} Experten vorsichtig${bullish ? `, ${bullish} positiv` : ''}`;
    }
  } else if (bullish > 0) {
    tone = 'positiv';
    satz = bullish === total ? `Alle ${total} Experten positiv` : `${bullish} von ${total} Experten positiv`;
  } else if (bearish > 0) {
    tone = 'negativ';
    satz = bearish === total ? `Alle ${total} Experten vorsichtig` : `${bearish} von ${total} Experten vorsichtig`;
  } else {
    tone = 'neutral';
    satz = `${total} Experten neutral`;
  }

  return { tone, satz, levelHinweis, bullish, bearish, neutral, experten: total, stimmen };
}

/** Ton → StatusDot-Farbe (eine Ampelsprache, wie in W97). */
export function consensusDot(tone: ConsensusTone): 'green' | 'yellow' | 'red' | 'gray' {
  const map = { positiv: 'green', negativ: 'red', uneinig: 'yellow', neutral: 'yellow', keine: 'gray' } as const;
  return map[tone];
}
